import { useState, useRef, useCallback, useEffect } from 'react';
import { createLogger } from '../../utils/logger';

// ログシステムの初期化
const logger = createLogger('QuizTimer');

/**
 * 改善されたクイズタイマーフック
 * - 競合状態（Race Condition）の解決
 * - メモリリーク対策
 * - シンプルな状態管理
 * - 堅牢なエラーハンドリング
 */
const useQuizTimer = () => {
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isActive, setIsActive] = useState(false);
  
  // タイマー管理用の ref
  const timerIdRef = useRef(null);
  const onTimeUpCallbackRef = useRef(null);
  const isCleanupRef = useRef(false);

  // タイマークリア（内部用）
  const clearTimer = useCallback(() => {
    if (timerIdRef.current) {
      clearInterval(timerIdRef.current);
      timerIdRef.current = null;
    }
    setIsActive(false);
  }, []);

  // タイマー開始
  const startTimer = useCallback((duration, onTimeUp) => {
    logger.info('タイマー開始', { duration: `${duration}秒`, hasCallback: !!onTimeUp });
    
    // バリデーション
    if (duration <= 0) {
      logger.warn('無効な制限時間:', duration);
      return;
    }

    if (!onTimeUp || typeof onTimeUp !== 'function') {
      logger.error('無効なコールバック関数:', typeof onTimeUp);
      return;
    }

    // 既存のタイマーをクリア（確実に停止）
    clearTimer();
    
    // クリーンアップフラグをリセット
    isCleanupRef.current = false;
    
    // 新しいタイマー設定（完全にリセット）
    setTimeRemaining(duration);
    setIsActive(true);
    onTimeUpCallbackRef.current = onTimeUp;
    isCleanupRef.current = false;

    logger.debug('タイマー設定完了', { 
      callbackType: typeof onTimeUpCallbackRef.current,
      initialTime: duration,
      isActive: true 
    });

    // タイマー実行
    timerIdRef.current = setInterval(() => {
      // クリーンアップ中なら実行しない
      if (isCleanupRef.current) {
        logger.debug('クリーンアップ中のため、タイマー処理をスキップ');
        return;
      }

      setTimeRemaining((prevTime) => {
        const newTime = Math.max(0, prevTime - 1);
        if (newTime % 10 === 0 || newTime <= 5) {
          logger.debug('タイマー更新', { remaining: `${newTime}秒` });
        }
        
        // 時間切れ処理
        if (newTime === 0) {
          logger.info('タイマー終了 - 時間切れ処理開始');
          
          // タイマークリア
          if (timerIdRef.current) {
            clearInterval(timerIdRef.current);
            timerIdRef.current = null;
          }
          setIsActive(false);
          
          // コールバック実行（非同期で安全に実行）
          const callback = onTimeUpCallbackRef.current;
          logger.debug('コールバック確認', { 
            type: typeof callback, 
            isValid: callback && typeof callback === 'function' 
          });
          
          if (callback && typeof callback === 'function') {
            logger.info('onTimeUp コールバック実行開始');
            // 次のtickで実行してスタックオーバーフローを防ぐ
            setTimeout(() => {
              try {
                if (!isCleanupRef.current) {
                  logger.debug('onTimeUp実行中...');
                  callback();
                  logger.debug('onTimeUp実行完了');
                } else {
                  logger.debug('クリーンアップ中のため、onTimeUp実行をスキップ');
                }
              } catch (error) {
                logger.error('onTimeUp実行エラー:', error);
              }
            }, 0);
          } else {
            logger.error('コールバック関数が無効です:', typeof callback);
          }
        }
        
        return newTime;
      });
    }, 1000);
  }, [clearTimer]);

  // タイマー停止
  const stopTimer = useCallback(() => {
    logger.info('タイマー停止');
    clearTimer();
    // コールバックはクリアしない（再開時に使用される可能性）
  }, [clearTimer]);

  // タイマーリセット
  const resetTimer = useCallback(() => {
    logger.info('タイマーリセット');
    clearTimer();
    setTimeRemaining(0);
    onTimeUpCallbackRef.current = null;
  }, [clearTimer]);

  // タイマー一時停止
  const pauseTimer = useCallback(() => {
    logger.info('タイマー一時停止');
    clearTimer();
    // timeRemainingとコールバックは保持
  }, [clearTimer]);

  // タイマー再開
  const resumeTimer = useCallback(() => {
    logger.info('タイマー再開', { remaining: `${timeRemaining}秒` });
    
    if (timeRemaining > 0 && onTimeUpCallbackRef.current) {
      startTimer(timeRemaining, onTimeUpCallbackRef.current);
    } else {
      logger.warn('タイマー再開失敗: 残り時間またはコールバックが無効');
    }
  }, [timeRemaining, startTimer]);

  // コンポーネントアンマウント時のクリーンアップ
  useEffect(() => {
    return () => {
      logger.info('タイマーフック クリーンアップ');
      isCleanupRef.current = true;
      clearTimer();
      onTimeUpCallbackRef.current = null;
    };
  }, [clearTimer]);

  return {
    timeRemaining,
    isActive,
    startTimer,
    stopTimer,
    resetTimer,
    pauseTimer,
    resumeTimer
  };
};

export default useQuizTimer; 